import { createStyles, Grid, makeStyles, Step, StepButton, Stepper, Theme, Typography, Paper } from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import { CustomConnector } from 'components/customConnector';
import { NotaComercialApiResponse, CustoEmissao, CadastroNotaComercialApiRequest, BancoResponse, FuncaoParticipante, ParticipanteResponse, RegistroStatus } from 'common/genericTypes';
import { MdSpace, SmSpace, Button, LgSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { resetMetadata } from 'store/modules/notaComercial/caracteristicas';
import { resetCriteriosRemuneracao } from 'store/modules/notaComercial/criteriosRemuneracao';
import { resetEventos } from 'store/modules/notaComercial/eventos';
import { resetDocuments } from 'store/modules/notaComercial/uploadDocumentos';
import { resetEmissor } from 'store/modules/notaComercial/emissor';
import { RootState } from 'store';
import NotaComercialParser from 'utils/parsers/notaComercialParser';
import FormularioValidarEmissor from 'pages/notaComercial/registro/formularioEmissorECodigo';
import FormularioCaracteristicas from 'pages/notaComercial/registro/formularioCaracteristicas';
import FormularioCriteriosRemuneracao from 'pages/notaComercial/registro/formularioCriteriosRemuneracao/criteriosRemuneracao';
import FormularioEventos from 'pages/notaComercial/registro/formularioEventosFinanceiros';
import { useValidarCampos } from 'pages/notaComercial/registro/useValidarCampos';
import { addAlert } from 'store/modules/snackBar';
import { highlightErrors } from 'store/modules/notaComercial';
import { EtapaCadastro } from 'common/registro/enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconWrapper: {
      width: '112px',
      height: '112px',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      width: '40px',
      height: '40px',
    },
  })
);

const NotaComercial: React.FC = () => {
  const caracteristicas = useSelector((state: RootState) => state.notaComercialCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.notaComercialCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.notaComercialCadastro.eventos);
  const emissor = useSelector((state: RootState) => state.notaComercialCadastro.emissor);

  const postInterOpNotaComercial = useSelector((state: RootState) => state.notaComercialApi.postInterOpNotaComercial);
  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantes);
  const getBancosApi = useSelector((state: RootState) => state.catalogoApi.getBancos);

  const [activeStep, setActiveStep] = useState<number>(-1);
  const [finished, setFinished] = useState<boolean>(false);
  const [bancos, setBancos] = useState<Array<BancoResponse>>([]);
  const [participantes, setParticipantes] = useState<Array<ParticipanteResponse>>([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const steps = ['1. Características da Emissão', '2. Critérios de Remuneração', '3. Eventos Financeiros'];
  const classes = useStyles();

  const camposInvalidosCE = useValidarCampos(EtapaCadastro.Caracteristicas);
  const camposInvalidosCR = useValidarCampos(EtapaCadastro.CriteriosRemuneracao);
  const camposInvalidosEF = useValidarCampos(EtapaCadastro.EventosFinanceiros);
  const camposInvalidosEmissor = useValidarCampos(EtapaCadastro.Emissor);

  const camposInvalidos = camposInvalidosCE
    .concat(camposInvalidosCR)
    .concat(camposInvalidosEF)
    .concat(camposInvalidosEmissor);

  const submitPostNotaComercial = async () => {
    if (camposInvalidos.length > 0) {
      dispatch(highlightErrors(true));
      dispatch(
        addAlert({
          message: `Os seguintes campos não foram preenchidos corretamente: ${camposInvalidos.join(', ')}.`,
          severity: 'error',
        })
      );
      return;
    } else {
      try {
        const notaComercial: CadastroNotaComercialApiRequest =
          NotaComercialParser.stateToCadastroNotaComercialApiRequest(
            caracteristicas,
            criteriosRemuneracao,
            eventos,
            undefined,
            emissor
          );
        const result = await postInterOpNotaComercial(dispatch, notaComercial, true);
        setFinished(true);
        return result;
      } catch (error) {
        return {} as NotaComercialApiResponse;
      }
    }
  };

  const handleNextStep = () => {
    if (activeStep === steps.length - 1) return;
    else setActiveStep(activeStep + 1);
  };

  const handlePreviousStep = () => {
    if (activeStep === 0) return;
    else setActiveStep(activeStep - 1);
  };

  const handleReset = () => {
    dispatch(resetMetadata());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(resetEmissor());
    dispatch(highlightErrors(false));
    setActiveStep(-1);
    setFinished(false);
    history.push('/solicitacoes');
  };

  useEffect(() => {
    dispatch(resetMetadata());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(resetEmissor());
    dispatch(highlightErrors(false));
  }, [resetMetadata, resetCriteriosRemuneracao, resetEventos, resetDocuments, resetEmissor]);

  const getStepContent = (step: number) => {
    switch (step) {
      case -1:
        return <FormularioValidarEmissor />;
      case 0:
        return <FormularioCaracteristicas participantes={participantes} bancos={bancos} />;
      case 1:
        return <FormularioCriteriosRemuneracao participantes={participantes} bancos={bancos} />;
      case 2:
        return <FormularioEventos />;
      default:
        return 'Unknown step';
    }
  };

  const isStepCompleted = (index: number) => {
    switch (index) {
      case -1:
        return camposInvalidosEmissor.length === 0;
      case 0:
        return camposInvalidosCE.length === 0;
      case 1:
        return camposInvalidosCR.length === 0;
      case 2:
        return camposInvalidosEF.length === 0;
      default:
        return false;
    }
  };

  const getParticipantes = useCallback(async () => {
    try {
      const result = await getParticipantesApi(
        dispatch,
        {
          funcoes: [FuncaoParticipante.Emissor, FuncaoParticipante.Escriturador, FuncaoParticipante.Custodiante],
          status: RegistroStatus.Aprovado,
          suspenso: false
        },
        true);
      setParticipantes(result);
    } catch (error) { }
  }, [dispatch]);

  const getBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, true);
      setBancos(typeof result === 'string' ? [] : result.banks);
    } catch (error) { }
  }, [dispatch]);

  useEffect(() => {
    getBancos();
  }, [getBancos]);

  useEffect(() => {
    getParticipantes();
  }, [getParticipantes]);

  return (
    <div>
      <Typography variant="h1">{`Cadastro de Nota Comercial`}</Typography>
      <MdSpace />
      <>
        {activeStep !== -1 && (
          <Stepper
            nonLinear
            activeStep={activeStep}
            connector={<CustomConnector />}
            style={{ padding: 0, display: 'flex', flexWrap: 'wrap' }}
          >
            {steps.map((label, index) => {
              return (
                <Step key={label} onClick={() => setActiveStep(index)}>
                  <StepButton completed={isStepCompleted(index)}>{label}</StepButton>
                </Step>
              );
            })}
          </Stepper>
        )}
        <MdSpace />
        <div>
          {finished ? (
            <Grid container direction="column" justify="center" alignItems="center">
              <div className={classes.iconWrapper}>
                <CheckCircle className={classes.icon} color="secondary" />
              </div>
              <SmSpace />
              <Typography variant="h3">{`Cadastro de Notas Comercial concluído com sucesso!`}</Typography>
              <SmSpace />
              <Typography variant="body2">{'Sua solicitação já está efetivada.'}</Typography>
              <MdSpace />
              <Button onClick={handleReset} variant="contained" color="primary">
                {'Voltar para a página inicial'}
              </Button>
              <MdSpace />
            </Grid>
          ) : (
            <>
              {getStepContent(activeStep)}
              <Paper
                style={{
                  position: 'fixed',
                  bottom: 0,
                  width: '100%',
                  marginLeft: '-40px',
                  backgroundColor: '#fff',
                  zIndex: 1,
                }}
              >
                <MdSpace />
                <Grid container justify="center" spacing={8} alignItems="center" style={{ width: '100%' }}>
                  <Grid item>
                    <Button variant="outlined" disabled={activeStep <= 0} onClick={handlePreviousStep}>
                      {'Passo Anterior'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant="outlined" disabled={camposInvalidosEmissor.length > 0} onClick={handleNextStep}>
                      {'Próximo Passo'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant="contained" color="primary" onClick={submitPostNotaComercial}>
                      {'Concluir'}
                    </Button>
                  </Grid>
                </Grid>
                <MdSpace />
              </Paper>
              <LgSpace />
            </>
          )}
        </div>
      </>
      <MdSpace />
    </div>
  );
};

export default NotaComercial;
